import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIsMeasurementPass } from '@/document-render/application/measurementPassContext';
import type { DocumentTypeVariableCategory } from '@/document-types/domain/types/DocumentType';
import type { RenderProseEditor } from './proseEditorSlot';

/**
 * THE CANVAS SELECTION, shared (LLAMS-610 Part 7). The panel (`FieldListEditor`'s Layers rows) and
 * the canvas (`DocumentRender`'s field elements) are two views of one block, so they read the same
 * id — an element is "selected" when it names the id both views agree on. Selecting a row in the
 * panel selects that element on the canvas, and vice versa (design ruling 2026-09-07, mirrored
 * from the composition-list precedent already shipped in Part 6's `LayersRow`).
 *
 * HARD RULE — HTML preview only, and no new cross-feature import. This context is:
 *   - never provided when `DocumentRender`'s `mode` is `'export'` (the caller that mounts a
 *     provider around the editor's canvas is responsible for this — this module has no `mode`
 *     concept of its own, so it cannot enforce it structurally, only by convention);
 *   - never imported by `document-pdf/**` — the PDF has no selection, no outline, no name tab;
 *   - self-contained within `document-render` — it owns its own "who has the caret" bridge
 *     (`registerCaretInsertHandler` / `insertAtCaret`) rather than importing the `blocks/`
 *     feature's `VariableFieldInsertContext`, which would be a NEW cross-feature import in the
 *     forbidden direction. `blocks/presentation` may import this module (the accepted
 *     `blocks → document-render` direction `LivePreview.tsx` already uses) to bridge the two.
 *
 * Every consumer reads it through `useOptionalEditableDocument()`, which returns `null` when no
 * provider is mounted — the default everywhere today, so this is fully inert until a future Part
 * (8) wraps a live editor's canvas + Layers panel in one `EditableDocumentProvider`.
 */
export interface EditableDocumentContextValue {
  selectedElementId: string | null;
  selectElement: (id: string | null) => void;
  /** Commits a field's edited text (canvas inline edit) back to the owning block content. */
  onFieldTextChange: (fieldId: string, nextValue: string) => void;
  /** `EditableFieldText` only: registers/clears the focused field's "insert at my caret" handler. */
  registerCaretInsertHandler: (handler: ((path: string) => void) | null) => void;
  /** Inserts a token into whichever field last registered a caret handler; a no-op otherwise. */
  insertAtCaret: (path: string) => void;
  /** Quote canvas sets this false so structure controls stay off the page. */
  structureEditable?: boolean;
  /**
   * `EditableFieldText`'s "insert a variable" affordance asks its host to open `CanvasValuePicker`
   * anchored to the given element — `document-render` cannot render that popover itself (it lives
   * in `blocks/presentation`, and `document-render` importing it would be the forbidden cross-
   * feature direction), so whoever mounts the provider (`BlockEditorPageView.tsx`) renders it here.
   */
  valuePickerAnchor: HTMLElement | null;
  requestValuePicker: (anchorEl: HTMLElement) => void;
  closeValuePicker: () => void;
  /**
   * The rich-text editor a selected prose Text element mounts in place (LLAMS-610 DB-05). Absent
   * (read-only canvas, or any mount that never supplies one) means the body is never editable.
   * Supplied by `ProseEditorSlotProvider`, never by the provider below.
   */
  renderProseEditor?: RenderProseEditor;
  /** Variable catalog so the canvas names a token element like the panel row does (Nit 1). */
  variableCategories?: DocumentTypeVariableCategory[];
}

const EditableDocumentContext = createContext<EditableDocumentContextValue | null>(null);

export interface EditableDocumentProviderProps {
  children: ReactNode;
  selectedElementId?: string | null;
  onSelectElement?: (id: string | null) => void;
  onFieldTextChange: (fieldId: string, nextValue: string) => void;
  structureEditable?: boolean;
  variableCategories?: DocumentTypeVariableCategory[];
}

/**
 * Controlled/uncontrolled hybrid, like a form input: pass `selectedElementId` +
 * `onSelectElement` to control selection from an ancestor (Part 8's shared Layers ↔ canvas
 * state); omit both for a self-contained canvas that owns its own selection.
 */
export function EditableDocumentProvider({
  children,
  selectedElementId,
  onSelectElement,
  onFieldTextChange,
  structureEditable = true,
  variableCategories
}: EditableDocumentProviderProps) {
  const [internalSelected, setInternalSelected] = useState<string | null>(null);
  const [valuePickerAnchor, setValuePickerAnchor] = useState<HTMLElement | null>(null);
  const isControlled = selectedElementId !== undefined;
  const resolvedSelected = isControlled ? (selectedElementId ?? null) : internalSelected;
  const caretHandlerRef = useRef<((path: string) => void) | null>(null);

  const registerCaretInsertHandler = useCallback((handler: ((path: string) => void) | null) => {
    caretHandlerRef.current = handler;
  }, []);

  const insertAtCaret = useCallback((path: string) => {
    caretHandlerRef.current?.(path);
  }, []);

  const value = useMemo<EditableDocumentContextValue>(
    () => ({
      selectedElementId: resolvedSelected,
      selectElement: (id: string | null) => {
        if (isControlled) {
          onSelectElement?.(id);
        } else {
          setInternalSelected(id);
        }
      },
      onFieldTextChange,
      structureEditable,
      variableCategories,
      registerCaretInsertHandler,
      insertAtCaret,
      valuePickerAnchor,
      requestValuePicker: setValuePickerAnchor,
      closeValuePicker: () => setValuePickerAnchor(null)
    }),
    [
      insertAtCaret,
      isControlled,
      onFieldTextChange,
      onSelectElement,
      registerCaretInsertHandler,
      resolvedSelected,
      structureEditable,
      valuePickerAnchor,
      variableCategories
    ]
  );

  return <EditableDocumentContext.Provider value={value}>{children}</EditableDocumentContext.Provider>;
}

/**
 * Overlays the prose editor slot onto the surrounding provider (LLAMS-610 DB-05) — same selection,
 * same callbacks, one more member. `BlockEditorCanvas` mounts it around its live preview only, so
 * the Sections panel and the canvas keep sharing one selection. A passthrough without a provider.
 */
export function ProseEditorSlotProvider({ renderProseEditor, children }: { renderProseEditor?: RenderProseEditor; children: ReactNode }) {
  const outer = useContext(EditableDocumentContext);
  const value = useMemo(() => (outer ? { ...outer, renderProseEditor } : null), [outer, renderProseEditor]);
  if (!value) return <>{children}</>;
  return <EditableDocumentContext.Provider value={value}>{children}</EditableDocumentContext.Provider>;
}

/**
 * Whether an editing provider is mounted at all — also inside the off-screen measurement pass,
 * where `useOptionalEditableDocument` returns `null`. For layout that must match the editing canvas
 * (e.g. a placeholder that replaces a body), never for editing chrome.
 */
export function useHasEditableDocument(): boolean {
  return useContext(EditableDocumentContext) !== null;
}

export function useOptionalEditableDocument(): EditableDocumentContextValue | null {
  const measuring = useIsMeasurementPass();
  const value = useContext(EditableDocumentContext);
  // The off-screen pagination pass shares this provider. Editing chrome there is a
  // second insert button and a textarea whose height never settles.
  if (measuring) return null;
  return value;
}
